// Marketplaces API client (Plan 9 FE). Self-contained typed wrappers over the
// brand-scoped Amazon surfaces: brands, library (products + keywords), the
// optimization queue (generate → preview → approve → rollback), trends +
// coverage gaps, and the AMC read views. Uses the shared `request` helper
// (HttpOnly-cookie auth, ApiError on non-2xx) from lib/api.ts.

import { request } from "../api";

// ── brands ────────────────────────────────────────────────────────────────
export interface BrandRow {
  slug: string;
  name: string;
  id?: string;
  /** Amazon marketplace codes this brand is bound to — the Portfolio's
   *  analog of the donor's `platforms` chips. */
  marketplaces?: string[];
  /** True when the brand has NO seller-account binding: its numbers come from
   *  the warehouse and no SP-API write can reach it.
   *
   *  Read this rather than testing `marketplaces.length === 0`. A binding with
   *  a NULL marketplace (the marketplace-agnostic default — what seed-dogfood
   *  writes for sand-sky and eb5) names no market, so a fully credentialed,
   *  writable brand ALSO has an empty `marketplaces` list. `undefined` means
   *  the API build predates the field; treat that as "not analytics-only", so
   *  an older API never mislabels a live brand as read-only. */
  analytics_only?: boolean;
  // Portfolio rollups (GET /api/brands?days=N). EVERY field is optional so
  // the page renders correctly against an API build that predates them:
  // counts fall back to 0, the sparkline to "—", health to dormant/healthy.
  products?: number;
  approved_keywords?: number;
  flagged_titles?: number;
  scanned_titles?: number;
  pending_previews?: number;
  /** Window sessions + the prior-window total. Null when the brand has no
   *  Brand-Analytics data for the window. */
  sessions?: number | null;
  prior_sessions?: number | null;
  // Commercial performance rollups (Portfolio v3). EVERY field is nullable AND
  // optional on purpose: `undefined` = the API build predates these fields,
  // `null` = the brand genuinely has no figure for the window. The page renders
  // "—" for both and never substitutes $0 / 0.00x, so a Portfolio pointed at an
  // API without the performance branch merged degrades to dashes rather than
  // asserting zeros that would read as "this brand sold nothing".
  /** Total marketplace sales for the window, USD. */
  total_sales?: number | null;
  prior_total_sales?: number | null;
  /** Advertising cost for the window, USD. */
  ad_spend?: number | null;
  prior_ad_spend?: number | null;
  /** Ad-attributed sales for the window, USD — the ROAS numerator. */
  ad_sales?: number | null;
  prior_ad_sales?: number | null;
  /** ad_sales / ad_spend, as a multiple (2.4 = 2.4x). */
  roas?: number | null;
  prior_roas?: number | null;
  /** ad_spend / total_sales. Unit is ambiguous across API builds, so the page
   *  derives the displayed TACoS from ad_spend + total_sales rather than
   *  reading these — see Portfolio.tsx `tacosOf`. */
  tacos?: number | null;
  prior_tacos?: number | null;
  /** Per-day series powering the Portfolio sparkline (daily sales, USD). */
  daily?: { date: string; sales?: number | null }[];
  // Only set when /api/brands is called with ?scope=all (SuperAdmin "All
  // orgs" Portfolio view). Lets the frontend group cross-org brands by their
  // owning org without a second round-trip.
  org_id?: string;
  org_name?: string;
}

// ── library ───────────────────────────────────────────────────────────────
export interface ProductRow {
  asin: string;
  marketplace: string;
  sku: string | null;
  title: string | null;
  bullets: string[];
  verified_claims: unknown[];
}
// Paged products response. `total` is the WHOLE catalogue under the marketplace
// filter (Sanuk US = 3,270), never the length of `items` — the "N of M" control
// and every count read from it.
export interface ProductsPage {
  items: ProductRow[];
  total: number;
  limit: number;
  offset: number;
}
export interface KeywordRow {
  id: string;
  keyword: string;
  marketplace: string;
  search_volume: number | null;
  compliance_status: string;
  // Present on the shared serializer (`_keyword_dict`). `relevance`/`source_type`/
  // `placement` come back on both the library and review lists.
  relevance?: number | null;
  source_type?: string | null;
  placement?: string | null;
  // Review-surface fields (Plan 12). These live on the KeywordLibrary row but the
  // current shared serializer omits them, so they're optional — the review UI
  // degrades gracefully (single group, no rank column) when they're absent.
  product_handle?: string | null;
  search_frequency_rank?: number | null;
  source_value?: string | null;
  term_type?: string | null;
  language?: string | null;
  rejection_reason?: string | null;
}

// ── keyword discovery (Plan 12) ─────────────────────────────────────────────
export type KeywordComplianceStatus =
  | "needs_review" | "approved" | "restricted" | "banned";

export interface KeywordJob {
  job_id: string;
  status: string;
  scope: "brand" | "product" | string;
}
// Paged keyword responses. `total` is the WHOLE matching set (Sanuk US =
// 50,001), never the length of `items` — the review pager and the "N keyword(s)"
// count read from it. The un-paged read shipped ~20MB; paging by default fixes it.
export interface ReviewKeywordsResponse {
  items: KeywordRow[];
  total: number;
  limit: number;
  offset: number;
}
export interface KeywordsPage {
  items: KeywordRow[];
  total: number;
  limit: number;
  offset: number;
}

// ── optimize ──────────────────────────────────────────────────────────────
export type OptimizationStage =
  | "queued" | "generating" | "verified" | "rejected"
  | "preview" | "approved" | "pushed" | "rolled_back" | "failed";

export interface QueueItem {
  id: string;
  asin: string;
  marketplace: string;
  sku: string | null;
  stage: OptimizationStage;
  self_audit_score: number | null;
  verifier_passed: boolean | null;
  change_log_id: string | null;
  created_at: string | null;
}
export interface HoldingWarning {
  asin: string;
  locked_until: string | null;
  source_change_id: string | null;
}
export interface QueueDetail extends QueueItem {
  generated: {
    title?: { text: string };
    bullets?: { text: string }[];
    search_terms?: string[];
  } | null;
  verifier_result: { passed?: boolean; reasons?: { offending: string }[] } | null;
  detail: Record<string, unknown> | null;
  error: string | null;
  // Non-blocking 7-day holding warning: present when this ASIN was pushed
  // within the window. The UI warns + offers "Proceed anyway"; never blocks.
  holding_warning: HoldingWarning | null;
}

// ── trends ────────────────────────────────────────────────────────────────
export type VerticalRelevance = "in_vertical" | "adjacent" | "off_vertical";

export interface TrendTermRow {
  term: string;
  method?: string;
  relevance?: string;
  current_rank?: number | null;
  metrics?: Record<string, unknown>;
  // Vertical relevance (Plan 13) — how related the term is to the tenant's
  // vertical. Backend defaults to "adjacent" (fail-open), so treat as present.
  vertical_relevance: VerticalRelevance;
  vertical_group: string | null;
}
export interface TrendsResponse {
  items: TrendTermRow[];
  // Count summary keyed by relevance/vertical bucket (present on the detect
  // response; optional on the plain list). Mirrors `by_relevance`.
  by_relevance?: Record<string, number>;
  by_vertical?: Record<string, number>;
  // How many rows the read-seam screen removed from `items` (list response
  // only): adult terms, and off-vertical opportunities for the brand's
  // industry. Observability, not a control — the rows are already gone.
  screened?: { adult: number; off_vertical: number };
}
export interface CoverageGapRow {
  query: string;
  impressions?: number;
}

// ── amc ───────────────────────────────────────────────────────────────────
export interface AmcQueryRow {
  id: string;
  query_type: string;
  status: string;
  time_window: string | null;
  created_at?: string | null;
}

export interface TitleComplianceFlag {
  asin: string;
  sku: string | null;
  title: string;
  length: number;
  cap: number;
  over_by: number;
  product_type: string | null;
}

export interface TitleComplianceResponse {
  marketplace: string;
  deadline: string;
  scanned: number;
  flagged: TitleComplianceFlag[];
  compliant: number;
}

export const mkt = {
  // brands. `days` requests the Portfolio rollups (the API clamps to [1,90]);
  // `allOrgs` is the SuperAdmin cross-org listing. Both omitted = the plain
  // brand list the Dashboard has always used, unchanged.
  //
  // Envelope-tolerant by design: this endpoint returns a BARE ARRAY today and
  // the Portfolio rollup work may move it to `{brands: [...]}`. Normalizing
  // here means the page renders against either shape and neither side has to
  // land first.
  listBrands: async (days?: number, allOrgs?: boolean): Promise<BrandRow[]> => {
    const params = new URLSearchParams();
    if (days) params.set("days", String(days));
    if (allOrgs) params.set("scope", "all");
    const qs = params.toString();
    const raw = await request<BrandRow[] | { brands: BrandRow[] }>(
      "GET",
      `/api/brands${qs ? `?${qs}` : ""}`,
    );
    return Array.isArray(raw) ? raw : (raw?.brands ?? []);
  },

  // library — paged (backend default 50/page, max 500). Omitting `params` still
  // works: the backend returns page one plus the whole-catalogue `total`.
  listProducts: (
    slug: string,
    marketplace?: string,
    params: { limit?: number; offset?: number } = {},
  ) => {
    const qs = new URLSearchParams();
    if (marketplace) qs.set("marketplace", marketplace);
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.offset != null) qs.set("offset", String(params.offset));
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<ProductsPage>("GET", `/api/brands/${slug}/library/products${suffix}`);
  },
  // Paged (backend default 50/page, max 500). `total` is the whole matching set.
  listKeywords: (
    slug: string,
    marketplace?: string,
    params: { approved_only?: boolean; limit?: number; offset?: number } = {},
  ) => {
    const qs = new URLSearchParams();
    if (marketplace) qs.set("marketplace", marketplace);
    if (params.approved_only) qs.set("approved_only", "true");
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.offset != null) qs.set("offset", String(params.offset));
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<KeywordsPage>("GET", `/api/brands/${slug}/library/keywords${suffix}`);
  },
  // July-27-2026 Amazon title cap — scan of EXISTING library titles (#55b).
  titleCompliance: (slug: string, marketplace = "US") =>
    request<TitleComplianceResponse>(
      "GET",
      `/api/brands/${slug}/library/title-compliance?marketplace=${encodeURIComponent(marketplace)}`,
    ),
  getProduct: (slug: string, asin: string, marketplace = "US") =>
    request<ProductRow & { search_terms?: string[] | null }>(
      "GET",
      `/api/brands/${slug}/library/products/${encodeURIComponent(asin)}?marketplace=${encodeURIComponent(marketplace)}`,
    ),

  // keyword discovery (Plan 12) — generate + the NEEDS_REVIEW → APPROVED review
  // surface. This is DISTINCT from `listKeywords` above (which hits
  // /library/keywords and returns only approved, optimizer-eligible terms).
  //
  // NOTE: `generate` returns a `job_id` for an async worker run. The additive
  // generate flow polls THIS review list and stops when new NEEDS_REVIEW rows
  // land. The regenerate-replace flow (`replace: true`) instead polls the
  // dedicated job-status route `GET /api/brands/{slug}/keywords/jobs/{job_id}`
  // (added in task #32 — see `getKeywordJob`) until the job is terminal, since a
  // replace purges the pool first and row counts can't be used as a done-signal.
  generateKeywords: (
    slug: string,
    body: { marketplace: string; asin?: string; handle?: string; replace?: boolean },
  ) => request<KeywordJob>("POST", `/api/brands/${slug}/keywords/generate`, body),
  listReviewKeywords: (
    slug: string,
    params: {
      marketplace?: string;
      status?: string;
      product_handle?: string;
      limit?: number;
      offset?: number;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.marketplace) qs.set("marketplace", params.marketplace);
    if (params.status) qs.set("status", params.status);
    if (params.product_handle) qs.set("product_handle", params.product_handle);
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.offset != null) qs.set("offset", String(params.offset));
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<ReviewKeywordsResponse>("GET", `/api/brands/${slug}/keywords${suffix}`);
  },
  // Bulk-promote NEEDS_REVIEW → APPROVED. The backend requires at least one
  // selector (`ids` and/or `product_handle`) — a brand-wide blind approve is
  // rejected (422), so callers pass explicit ids or a product_handle.
  approveKeywords: (
    slug: string,
    body: { ids?: string[]; product_handle?: string; marketplace?: string },
  ) => request<{ approved: number }>("POST", `/api/brands/${slug}/keywords/approve`, body),
  rejectKeywords: (slug: string, body: { ids: string[] }) =>
    request<{ rejected: number }>("POST", `/api/brands/${slug}/keywords/reject`, body),
  editKeyword: (slug: string, id: string, body: { placement?: string }) =>
    request<KeywordRow>(
      "PATCH",
      `/api/brands/${slug}/keywords/${encodeURIComponent(id)}`,
      body,
    ),
  // Hard-delete keyword candidates (task #32). The backend always requires an
  // explicit selector — pass `ids` (per-row) or `status`/`product_handle`/
  // `marketplace` (bulk). Destructive + unrecoverable: callers window.confirm first.
  deleteKeywords: (
    slug: string,
    body: { ids?: string[]; product_handle?: string; marketplace?: string; status?: string },
  ) => request<{ deleted: number }>("DELETE", `/api/brands/${slug}/keywords`, body),
  // Job-status for an async discovery run (task #32). Regenerate-replace polls
  // this until `status` is terminal (succeeded|failed).
  getKeywordJob: (slug: string, jobId: string) =>
    request<{
      id: string;
      status: string;
      attempts: number;
      max_attempts: number;
      result_json: unknown;
      last_error: string | null;
      created_at: string | null;
    }>("GET", `/api/brands/${slug}/keywords/jobs/${encodeURIComponent(jobId)}`),

  // optimize
  listQueue: (slug: string, stage?: string) => {
    const qs = stage ? `?stage=${encodeURIComponent(stage)}` : "";
    return request<{ items: QueueItem[] }>("GET", `/api/brands/${slug}/optimize/queue${qs}`);
  },
  getQueueItem: (slug: string, id: string) =>
    request<QueueDetail>("GET", `/api/brands/${slug}/optimize/${id}`),
  generate: (slug: string, asin: string, marketplace = "US") =>
    request<QueueDetail>("POST", `/api/brands/${slug}/optimize/generate`, { asin, marketplace }),
  approve: (slug: string, id: string, override = false) =>
    request<QueueItem>("POST", `/api/brands/${slug}/optimize/${id}/approve`, { override }),
  rollback: (slug: string, id: string) =>
    request<QueueItem>("POST", `/api/brands/${slug}/optimize/${id}/rollback`),

  // trends + coverage gaps
  listTrends: (slug: string, marketplace = "US") =>
    request<TrendsResponse>(
      "GET", `/api/brands/${slug}/trends?marketplace=${encodeURIComponent(marketplace)}`,
    ),
  detectTrends: (slug: string, marketplace = "US") =>
    request<TrendsResponse>(
      "POST", `/api/brands/${slug}/trends/detect`, { marketplace },
    ),
  coverageGaps: (slug: string, asin: string, marketplace = "US") =>
    request<{ asin: string; coverage_gaps: CoverageGapRow[] }>(
      "GET",
      `/api/brands/${slug}/coverage-gaps?asin=${encodeURIComponent(asin)}&marketplace=${encodeURIComponent(marketplace)}`,
    ),

  // amc (org-scoped read view)
  listAmcQueries: () => request<{ items: AmcQueryRow[] }>("GET", "/api/amc/queries"),
};
